import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { supabase } from '../lib/supabase';
import { pessoaService } from './pessoaService';
import { fixEncodingIssues } from '../utils/textUtils';
import { propostaMetaService } from './propostaMetaService';
import { buscarModeloAtivo, gerarPdfDeModelo } from './modeloRendererService';

export async function generatePropostaPdf(rawProposta: any): Promise<void> {
  const proposta = propostaMetaService.enrichProposta(rawProposta);
  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) {
      const today = new Date();
      return today.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    }
    try {
      const parts = dateStr.split('T')[0].split('-');
      if (parts.length === 3) {
        const months = [
          'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
          'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
        ];
        const day = parseInt(parts[2], 10);
        const monthIndex = parseInt(parts[1], 10) - 1;
        const year = parts[0];
        return `São Paulo, ${day} de ${months[monthIndex]} de ${year}`;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const proposalDate = formatDate(proposta.created_at);

  // Fetch client detailed info (contacts and addresses)
  let clientObra = proposta.pessoa?.nome || 'Cliente não informado';
  let clientContact = 'Eric Jonson';
  let clientAddress = 'Alameda Terracota, 150 - São Caetano do Sul';

  try {
    const pessoaId = proposta.pessoa_id;
    if (pessoaId) {
      const contatos: any[] = await pessoaService.getContatosByPessoa(pessoaId);
      const enderecos: any[] = await pessoaService.getEnderecosByPessoa(pessoaId);
      
      if (contatos && contatos.length > 0) {
        const primaryContact = contatos.find(c => c.principal === 'S' || c.principal === 'Sim') || contatos[0];
        clientContact = primaryContact.nome || 'Responsável';
      } else {
        clientContact = 'À Diretoria';
      }

      if (enderecos && enderecos.length > 0) {
        const primaryAddress = enderecos.find(e => e.principal === 'S' || e.principal === 'Sim') || enderecos[0];
        const rua = primaryAddress.rua || '';
        const numero = primaryAddress.numero ? `, ${primaryAddress.numero}` : '';
        const bairro = primaryAddress.bairro ? ` - ${primaryAddress.bairro}` : '';
        const cidadeNome = primaryAddress.cidade?.nome || '';
        clientAddress = `${rua}${numero}${bairro} - ${cidadeNome}`;
      } else {
        clientAddress = 'Endereço não cadastrado';
      }
    }
  } catch (error) {
    console.error('Erro ao buscar dados adicionais do cliente para o PDF:', error);
  }

  // Create temporary container for PDF pages
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '794px'; // Standard A4 width at 96 DPI
  container.style.display = 'flex';
  container.style.flexDirection = 'column';

  // Total proposal calculation
  const items = proposta.detalhes_do_pedido || [];
  const total = proposta.total_geral || items.reduce((acc: number, item: any) => {
    const itemTotal = Number(item.valor_total) || (Number(item.quantidade || 0) * Number(item.valor_unitario || 0));
    return acc + itemTotal;
  }, 0);

  // Se existir um modelo de "Proposta" ativo no Gerenciador de Modelos, ele é
  // usado para gerar o PDF (é o que o usuário desenhou/editou lá). Sem modelo
  // ativo, cai no layout fixo abaixo (comportamento de sempre).
  const modeloAtivo = await buscarModeloAtivo('proposta');
  if (modeloAtivo) {
    const primeiroItem = items[0];
    const formatCurrencyPlano = (val?: number) => formatCurrency(val).replace('R$', '').trim();

    const dadosProposta: Record<string, string> = {
      PROPOSTA_REF: String(proposta.id || ''),
      DATA_EMISSAO: proposalDate,
      VALIDADE_PROPOSTA: '15 dias',

      CLIENTE_NOME: clientObra,
      CLIENTE_DOCUMENTO: proposta.pessoa?.documento || '',
      CLIENTE_EMAIL: proposta.pessoa?.email || '',
      CLIENTE_TELEFONE: proposta.pessoa?.fone || '',
      CLIENTE_ENDERECO: clientAddress,
      SOLICITANTE: clientContact,
      TELEFONE_SOLICITANTE: proposta.pessoa?.fone || '',

      SERVICO_DESCRICAO: primeiroItem
        ? fixEncodingIssues(primeiroItem.servicos || 'Serviço de transporte')
        : 'Prestação de serviços de transporte e logística',
      SERVICO_QTD: primeiroItem ? String(Number(primeiroItem.quantidade) || 1) : '1',
      SERVICO_VALOR: primeiroItem
        ? formatCurrencyPlano(Number(primeiroItem.valor_total) || Number(primeiroItem.valor_unitario) || 0)
        : formatCurrencyPlano(total),

      VALOR_TAXAS: '0,00',
      VALOR_TOTAL: formatCurrencyPlano(total),
      FORMA_PAGAMENTO: proposta.condicoes_pagamento || 'Faturamento quinzenal, boleto com vencimento em 15 dias',
      OBSERVACOES: proposta.consideracoes || '',
    };

    // Uma linha por item da proposta (campo {{TABELA_ITENS}} do modelo)
    const itensTabela = items.map((item: any) => {
      const qtd = Number(item.quantidade) || 0;
      const unit = Number(item.valor_unitario) || 0;
      return {
        descricao: fixEncodingIssues(item.servicos || 'Serviço de transporte'),
        quantidade: `${qtd} ${item.unidade_medida || ''}`.trim(),
        valorUnitario: formatCurrency(unit),
        valorTotal: formatCurrency(Number(item.valor_total) || qtd * unit),
      };
    });

    try {
      await gerarPdfDeModelo(
        modeloAtivo,
        dadosProposta,
        `Proposta_Comercial_VOOPE_#${proposta.id || 'N_A'}.pdf`,
        itensTabela
      );
      return;
    } catch (err) {
      console.error('Erro ao gerar PDF a partir do modelo customizado, usando layout padrão embutido:', err);
      // segue para o fallback abaixo
    }
  }

  // Helper for generating table rows
  let tableRowsHtml = '';
  items.forEach((item: any, idx: number) => {
    const unitMap = item.unidade_medida || 'VJ';
    const desc = fixEncodingIssues(item.servicos || 'Serviço de transporte');
    const qtd = Number(item.quantidade) || 0;
    const unitPrice = Number(item.valor_unitario) || 0;
    const itemTotal = Number(item.valor_total) || (qtd * unitPrice);
    const isEven = idx % 2 === 0;
    const rowBg = isEven ? '#f8fafc' : '#ffffff';

    tableRowsHtml += `
      <tr style="background-color: ${rowBg}; border-bottom: 1px solid #e2e8f0; height: 42px;">
        <td style="border-right: 1px solid #e2e8f0; padding: 10px; text-align: center; font-weight: bold; color: #475569; font-size: 11px;">${unitMap}</td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px 14px; text-align: left; color: #1e293b; font-size: 11.5px; font-weight: 500;">${desc}</td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px; text-align: center; color: #1e293b; font-size: 11.5px;">${qtd}</td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px 12px; text-align: right; color: #475569; font-size: 11px; font-family: monospace;">${formatCurrency(unitPrice)}</td>
        <td style="padding: 10px 14px; text-align: right; font-weight: bold; color: #000000; font-size: 12px; font-family: monospace;">${formatCurrency(itemTotal)}</td>
      </tr>
    `;
  });

  // Fill up with empty rows if less than 5 to match the template structure beautifully
  const emptyRowsNeeded = Math.max(0, 5 - items.length);
  for (let i = 0; i < emptyRowsNeeded; i++) {
    const isEven = (items.length + i) % 2 === 0;
    const rowBg = isEven ? '#f8fafc' : '#ffffff';
    tableRowsHtml += `
      <tr style="background-color: ${rowBg}; border-bottom: 1px solid #e2e8f0; height: 42px;">
        <td style="border-right: 1px solid #e2e8f0; padding: 10px;"></td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px;"></td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px;"></td>
        <td style="border-right: 1px solid #e2e8f0; padding: 10px; text-align: right; color: #94a3b8; font-size: 11px; font-family: monospace;">R$ -</td>
        <td style="padding: 10px; text-align: right; color: #94a3b8; font-size: 11px; font-family: monospace;">R$ -</td>
      </tr>
    `;
  }

  container.innerHTML = `
    <!-- PAGE 1: COVER -->
    <div id="proposta-page-1" style="width: 794px; height: 1123px; background-color: #ffffff; position: relative; box-sizing: border-box; padding: 80px 60px; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; page-break-after: always; border: 1px solid #cbd5e1; font-family: 'Arial', sans-serif;">
      
      <!-- Top header strip & Decorative Elements -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%;">
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="width: 4px; height: 35px; background-color: #dc2626; border-radius: 2px;"></div>
          <span style="font-size: 12px; font-weight: bold; color: #475569; letter-spacing: 2px; text-transform: uppercase;">COMERCIAL</span>
        </div>
        <div style="background-color: #000000; padding: 12px 30px; border-top: 4px solid #dc2626; text-align: center; border-radius: 4px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          <span style="color: #ffffff; font-size: 13px; font-weight: bold; letter-spacing: 2px; text-transform: uppercase;">Proposta Comercial</span>
        </div>
      </div>

      <!-- Main Central Logo -->
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; margin-top: 40px; margin-bottom: 40px;">
        <div style="position: relative; display: flex; align-items: center; justify-content: center; width: 220px; height: 220px; margin-bottom: 30px;">
          <!-- Elegant background geometric badge -->
          <div style="position: absolute; width: 100%; height: 100%; border: 1.5px solid #f1f5f9; border-radius: 50%; transform: scale(1.1);"></div>
          <div style="position: absolute; width: 100%; height: 100%; border: 3px double #e2e8f0; border-radius: 50%;"></div>
          
          <svg width="110" height="110" viewBox="0 0 100 100" style="z-index: 10;">
            <!-- Custom crisp SVG reflecting VOOPE TWO geometric brand -->
            <path d="M15 70 L50 32 L85 70" stroke="#dc2626" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" fill="none" />
            <path d="M36 53 L50 38 L64 53" stroke="#dc2626" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" fill="none" />
          </svg>
        </div>

        <div style="font-size: 42px; font-weight: 800; color: #0f172a; letter-spacing: 3px; text-align: center; line-height: 1.1; margin-bottom: 4px;">
          VOOPE <span style="font-weight: 900; color: #dc2626;">TWO</span>
        </div>
        <div style="font-size: 13px; font-weight: bold; color: #475569; letter-spacing: 8px; text-transform: uppercase; margin-bottom: 24px;">
          TRANSPORTES
        </div>
        <div style="width: 80px; height: 2px; background-color: #cbd5e1; border-radius: 1px;"></div>
      </div>

      <!-- Footer message and Decorative line -->
      <div style="display: flex; flex-direction: column; align-items: center; width: 100%; gap: 30px;">
        <div style="width: 100%; height: 1px; background: linear-gradient(to right, transparent, #e2e8f0 15%, #e2e8f0 85%, transparent);"></div>
        <div style="text-align: center;">
          <span style="font-size: 15px; font-weight: bold; color: #dc2626; letter-spacing: 2px; text-transform: uppercase; background-color: #fef2f2; padding: 8px 24px; border-radius: 30px; border: 1px solid #fee2e2;">
            Transporte de eficiência e qualidade.
          </span>
        </div>
        <div style="font-size: 10px; color: #94a3b8; font-weight: 500; letter-spacing: 1px;">© 2026 VOOPE TWO. Todos os direitos reservados.</div>
      </div>
    </div>

    <!-- PAGE 2: INSTITUTIONAL & PARTNERS -->
    <div id="proposta-page-2" style="width: 794px; height: 1123px; background-color: #ffffff; position: relative; box-sizing: border-box; padding: 70px 60px; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; page-break-after: always; border: 1px solid #cbd5e1; font-family: 'Arial', sans-serif;">
      
      <div>
        <!-- Small top header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 35px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px;">
          <span style="font-size: 11px; font-weight: bold; color: #94a3b8; letter-spacing: 1.5px; text-transform: uppercase;">Quem Somos & Diferenciais</span>
          <span style="font-size: 11px; font-weight: bold; color: #dc2626; letter-spacing: 1px;">VOOPE TWO</span>
        </div>

        <!-- Institutional Text -->
        <p style="font-size: 14.5px; color: #334155; line-height: 1.7; text-align: justify; margin-bottom: 20px; text-indent: 20px;">
          A <strong style="color: #dc2626; font-weight: 700;">Voope</strong> está presente no mercado de transporte de cargas para construção civil desde 2015, consolidando-se como referência de pontualidade, segurança e excelência operacional.
        </p>
        <p style="font-size: 14.5px; color: #334155; line-height: 1.7; text-align: justify; margin-bottom: 40px; text-indent: 20px;">
          Podemos contribuir de forma altamente positiva para sua empresa, com serviços realizados rigorosamente em conformidade com as normas técnicas de qualidade e segurança. Prezamos pelo atendimento ágil, garantindo segurança jurídica, controle operacional rígido e a plena satisfação de nossos parceiros comerciais.
        </p>

        <!-- Core Values Row (5 items) with gorgeous visual layout -->
        <div style="display: flex; justify-content: space-between; align-items: stretch; gap: 12px; margin-bottom: 45px;">
          <!-- Item 1: Inovação -->
          <div style="flex: 1; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 10px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <div style="width: 44px; height: 44px; border-radius: 12px; background-color: #fff7ed; border: 1px solid #ffedd5; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1 .3 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                <path d="M9 18h6M10 22h4" />
              </svg>
            </div>
            <span style="font-size: 11px; font-weight: bold; color: #1e293b; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px;">Inovação</span>
          </div>

          <!-- Item 2: Prazos -->
          <div style="flex: 1; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 10px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <div style="width: 44px; height: 44px; border-radius: 12px; background-color: #fff7ed; border: 1px solid #ffedd5; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <span style="font-size: 11px; font-weight: bold; color: #1e293b; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px;">Cumprimento<br>de prazos</span>
          </div>

          <!-- Item 3: Qualidade -->
          <div style="flex: 1; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 10px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <div style="width: 44px; height: 44px; border-radius: 12px; background-color: #fff7ed; border: 1px solid #ffedd5; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <circle cx="12" cy="8" r="7" />
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
              </svg>
            </div>
            <span style="font-size: 11px; font-weight: bold; color: #1e293b; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px;">Garantia de<br>Qualidade</span>
          </div>

          <!-- Item 4: Valorização -->
          <div style="flex: 1; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 10px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <div style="width: 44px; height: 44px; border-radius: 12px; background-color: #fff7ed; border: 1px solid #ffedd5; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <span style="font-size: 11px; font-weight: bold; color: #1e293b; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px;">Valorização<br>de Pessoas</span>
          </div>

          <!-- Item 5: Sustentabilidade -->
          <div style="flex: 1; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px 10px; display: flex; flex-direction: column; align-items: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.02);">
            <div style="width: 44px; height: 44px; border-radius: 12px; background-color: #fff7ed; border: 1px solid #ffedd5; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M2 12h20" />
              </svg>
            </div>
            <span style="font-size: 11px; font-weight: bold; color: #1e293b; line-height: 1.3; text-transform: uppercase; letter-spacing: 0.5px;">Responsabilidade<br>Social</span>
          </div>
        </div>

        <!-- Solid Red separator line -->
        <div style="width: 100%; height: 3px; background: linear-gradient(to right, #dc2626, #f43f5e); border-radius: 2px; margin-bottom: 30px;"></div>

        <!-- Differential box with premium typography -->
        <div style="background-color: #f0fdf4; padding: 18px 24px; border-radius: 8px; text-align: center; margin-bottom: 30px; border: 1px solid #bbf7d0;">
          <p style="font-size: 13px; font-weight: bold; color: #166534; line-height: 1.5; margin: 0; letter-spacing: 0.2px;">
            Desenvolver relacionamentos sólidos e transparentes é nosso maior compromisso. Agradecemos às grandes marcas que confiam em nosso padrão operacional:
          </p>
        </div>

        <!-- Partners Grid (16 items) - Premium polished cards -->
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;">
          <!-- CAMBUR -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <div style="font-size: 11px; font-weight: 900; color: #991b1b; letter-spacing: 1px;">CAMBUR</div>
            <div style="font-size: 6px; color: #64748b; font-weight: bold; text-transform: uppercase;">CONSTRUTORA</div>
          </div>
          <!-- CURY -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px; gap: 3px;">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0284c7" stroke-width="3">
              <path d="M22 2L2 22M2 2l20 20" />
            </svg>
            <span style="font-size: 11.5px; font-weight: 800; color: #0284c7; letter-spacing: 0.5px;">CURY</span>
          </div>
          <!-- STOC -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px; gap: 2px;">
            <span style="font-size: 12px; font-weight: 900; color: #16a34a; letter-spacing: 1px;">STOG</span>
            <div style="width: 5px; height: 5px; background-color: #16a34a; border-radius: 50%;"></div>
          </div>
          <!-- VIBRA -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <div style="font-size: 11px; font-weight: bold; color: #ea580c; display: flex; align-items: center; gap: 2px;">
              vibra <span style="font-size: 11px; color: #f97316;">☀️</span>
            </div>
            <div style="font-size: 6px; color: #64748b; font-weight: bold; text-transform: uppercase;">RESIDENCIAL</div>
          </div>

          <!-- EVEN -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
            <span style="font-size: 13px; font-weight: 800; color: #0891b2; font-family: sans-serif;">even</span>
          </div>
          <!-- PORTOFERRAZ -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); gap: 4px; padding: 4px;">
            <div style="width: 11px; height: 11px; background: linear-gradient(135deg, #1d4ed8 50%, #eab308 50%); border-radius: 1px;"></div>
            <span style="font-size: 9px; font-weight: bold; color: #1e3a8a;">portoferraz</span>
          </div>
          <!-- NORTIS -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
            <span style="font-size: 11px; font-weight: bold; color: #000000; letter-spacing: 0.5px;">NORTIS <sup>®</sup></span>
          </div>
          <!-- ECON -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); gap: 3px;">
            <span style="font-size: 11px; font-weight: bold; color: #15803d;">econ</span>
            <div style="width: 10px; height: 10px; background-color: #15803d; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
              <span style="font-size: 6px; color: white; font-weight: bold;">E</span>
            </div>
          </div>

          <!-- ROCONTEC -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 10px; font-weight: bold; color: #991b1b; letter-spacing: -0.2px;">Rocontec</span>
            <span style="font-size: 5.5px; color: #475569; font-weight: bold; text-transform: uppercase;">Construção</span>
          </div>
          <!-- ATHIE WOHNRATH -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 9.5px; font-weight: bold; color: #000000; letter-spacing: -0.2px;">athié | wohnrath</span>
          </div>
          <!-- BN ENGENHARIA -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 11px; font-weight: 900; color: #1e3a8a; line-height: 1;">BN</span>
            <span style="font-size: 6px; color: #475569; font-weight: bold; text-transform: uppercase;">Engenharia</span>
          </div>
          <!-- AM4AM -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 11px; font-weight: bold; color: #dc2626;">AM4AM</span>
            <span style="font-size: 5.5px; color: #64748b; font-weight: bold; text-transform: uppercase;">Incorporadora</span>
          </div>

          <!-- TROVATA -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 9px; font-weight: bold; color: #854d0e; letter-spacing: 0.5px;">TROVATA</span>
            <span style="font-size: 5.5px; color: #64748b; font-weight: bold;">CONSTRUTORA</span>
          </div>
          <!-- B'BORGES -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
            <span style="font-size: 10.5px; font-weight: bold; color: #166534;">b'borges</span>
          </div>
          <!-- GAFISA -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
            <span style="font-size: 11.5px; font-weight: 800; color: #991b1b; letter-spacing: 0.2px;">Gafisa</span>
          </div>
          <!-- SETIN -->
          <div style="height: 52px; background: white; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 4px;">
            <span style="font-size: 10.5px; font-weight: bold; color: #1d4ed8;">SETIN<sup>®</sup></span>
            <span style="font-size: 5.5px; color: #64748b; font-weight: bold; text-transform: uppercase;">Incorporadora</span>
          </div>
        </div>
      </div>

      <!-- Footer with Page 2 mark -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px;">
        <span style="font-size: 10px; color: #94a3b8; font-weight: bold;">VOOPE TWO TRANSPORTES</span>
        <div style="background-color: #0f172a; color: #ffffff; font-weight: bold; font-size: 12px; width: 30px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 4px;">
          2
        </div>
      </div>
    </div>

    <!-- PAGE 3: OBJECT, SCOPE, AND PRICE TABLE -->
    <div id="proposta-page-3" style="width: 794px; height: 1123px; background-color: #ffffff; position: relative; box-sizing: border-box; padding: 70px 60px; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; page-break-after: always; border: 1px solid #cbd5e1; font-family: 'Arial', sans-serif;">
      <div>
        <!-- Small top header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px;">
          <span style="font-size: 11px; font-weight: bold; color: #94a3b8; letter-spacing: 1.5px; text-transform: uppercase;">Escopo & Tabela de Preços</span>
          <span style="font-size: 11px; font-weight: bold; color: #dc2626; letter-spacing: 1px;">VOOPE TWO</span>
        </div>

        <!-- Date & Metadata Card -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; gap: 20px;">
          <div style="flex: 2; display: flex; flex-direction: column; gap: 6px;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 11px; font-weight: 800; color: #dc2626; letter-spacing: 1px; text-transform: uppercase;">CLIENTE DESTINATÁRIO</span>
            </div>
            <div style="font-size: 15px; font-weight: bold; color: #0f172a;">${clientObra}</div>
            <div style="font-size: 12px; color: #475569;"><strong style="color: #1e293b;">A/C:</strong> ${clientContact}</div>
            <div style="font-size: 12px; color: #475569;"><strong style="color: #1e293b;">Endereço:</strong> ${clientAddress}</div>
          </div>
          <div style="flex: 1; text-align: right; display: flex; flex-direction: column; gap: 4px; border-left: 1px solid #e2e8f0; padding-left: 20px; height: 100%;">
            <span style="font-size: 10px; font-weight: bold; color: #64748b; letter-spacing: 0.5px;">EMISSÃO DA PROPOSTA</span>
            <div style="font-size: 12px; font-weight: bold; color: #0f172a; margin-top: 2px;">${proposalDate}</div>
            <div style="font-size: 11px; color: #64748b; margin-top: 8px;">Validade: 15 dias</div>
          </div>
        </div>

        <!-- OBJETO & ESCOPO sections side-by-side or stacked cleanly -->
        <div style="display: flex; gap: 20px; margin-bottom: 25px;">
          <div style="flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background: #ffffff;">
            <div style="font-size: 10px; font-weight: bold; color: #dc2626; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;">
              OBJETO DO CONTRATO
            </div>
            <div style="font-size: 11.5px; color: #334155; line-height: 1.5; font-weight: 500;">
              Constitui objeto do contrato a prestação de serviços de transporte de cargas e movimentação de materiais para Construção Civil.
            </div>
          </div>
          <div style="flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background: #ffffff;">
            <div style="font-size: 10px; font-weight: bold; color: #dc2626; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;">
              ESCOPO DOS SERVIÇOS
            </div>
            <div style="font-size: 11.5px; color: #334155; line-height: 1.5; font-weight: 500;">
              Locação de veículos do tipo caminhão, TRUCK, TOCO, MUNK, HR e PICAPE conforme itens apontados na Tabela de Preços.
            </div>
          </div>
        </div>

        <!-- Outside of scope card -->
        <div style="background-color: #fff1f2; border: 1px solid #fecdd3; border-radius: 8px; padding: 12px 16px; margin-bottom: 25px; display: flex; align-items: center; gap: 10px;">
          <div style="font-size: 10px; font-weight: bold; color: #e11d48; text-transform: uppercase; letter-spacing: 1px;">
            FORA DO ESCOPO:
          </div>
          <div style="font-size: 11.5px; font-weight: bold; color: #9f1239;">
            Seguro de Cargas (Será de responsabilidade integral do contratante, salvo pactuado em contrário)
          </div>
        </div>

        <!-- PRICE TABLE -->
        <div style="margin-bottom: 25px; border: 1.5px solid #0f172a; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
          <div style="background-color: #0f172a; padding: 12px 16px; text-align: center; color: #ffffff; font-size: 12px; font-weight: bold; letter-spacing: 1.5px; text-transform: uppercase;">
            Tabela de Preços e Serviços — São Paulo / SP
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background-color: #dc2626; color: #ffffff; height: 36px;">
                <th style="width: 10%; border-right: 1px solid rgba(255,255,255,0.2); padding: 6px; text-align: center; font-weight: bold; letter-spacing: 0.5px;">U.M</th>
                <th style="width: 52%; border-right: 1px solid rgba(255,255,255,0.2); padding: 6px 14px; text-align: left; font-weight: bold; letter-spacing: 0.5px;">DESCRIÇÃO DO SERVIÇO / VEÍCULO</th>
                <th style="width: 10%; border-right: 1px solid rgba(255,255,255,0.2); padding: 6px; text-align: center; font-weight: bold; letter-spacing: 0.5px;">QTD</th>
                <th style="width: 14%; border-right: 1px solid rgba(255,255,255,0.2); padding: 6px 12px; text-align: right; font-weight: bold; letter-spacing: 0.5px;">VALOR UNIT.</th>
                <th style="width: 14%; padding: 6px 14px; text-align: right; font-weight: bold; letter-spacing: 0.5px;">VALOR TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${tableRowsHtml}
              <!-- Total row -->
              <tr style="background-color: #0f172a; color: #ffffff; height: 44px; font-weight: bold;">
                <td colspan="3" style="padding: 10px 16px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold;">VALOR TOTAL GERAL DO FRETE</td>
                <td style="padding: 10px 12px; text-align: right; font-size: 11px; color: #94a3b8; font-weight: normal;">R$</td>
                <td style="padding: 10px 16px; text-align: right; font-size: 14px; font-weight: 900; color: #ffffff; font-family: monospace;">${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Considerations box -->
        <div style="border: 1px solid #cbd5e1; background-color: #f8fafc; border-radius: 8px; padding: 16px;">
          <div style="font-size: 11px; font-weight: bold; color: #0f172a; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
            CONSIDERAÇÕES IMPORTANTES DESTA TABELA:
          </div>
          <div style="font-size: 11px; color: #334155; line-height: 1.6;">
            ${proposta.consideracoes ? proposta.consideracoes.replace(/\n/g, '<br>') : `
            • Veículos considerados por viagem de ida e volta, valores expressos para carga e descarga no mesmo dia útil.<br>
            • Os veículos contam com liberação operacional e acesso regulamentado para Áreas de Restrição até as 15:00.<br>
            • No item 01, os custos regulamentares de pedágios intermunicipais encontram-se inclusos na tarifa.
            `.trim()}
          </div>
        </div>
      </div>

      <!-- Footer with Page 3 mark -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px;">
        <span style="font-size: 10px; color: #94a3b8; font-weight: bold;">VOOPE TWO TRANSPORTES</span>
        <div style="background-color: #0f172a; color: #ffffff; font-weight: bold; font-size: 12px; width: 30px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 4px;">
          3
        </div>
      </div>
    </div>

    <!-- PAGE 4: COMMERCIAL CONDITIONS & SIGNATURES -->
    <div id="proposta-page-4" style="width: 794px; height: 1123px; background-color: #ffffff; position: relative; box-sizing: border-box; padding: 70px 60px; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; border: 1px solid #cbd5e1; font-family: 'Arial', sans-serif;">
      <div>
        <!-- Small top header -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px;">
          <span style="font-size: 11px; font-weight: bold; color: #94a3b8; letter-spacing: 1.5px; text-transform: uppercase;">Cláusulas & Condições Comerciais</span>
          <span style="font-size: 11px; font-weight: bold; color: #dc2626; letter-spacing: 1px;">VOOPE TWO</span>
        </div>

        <!-- Custom conditions or default detailed fallback -->
        <div style="max-height: 550px; overflow: hidden; font-size: 10.5px; color: #1e293b; line-height: 1.5; text-align: justify; padding-right: 5px;">
          ${proposta.condicoes_comerciais ? `
            <div style="white-space: pre-wrap; font-size: 11px; line-height: 1.6; color: #334155;">${proposta.condicoes_comerciais}</div>
          ` : `
            <div style="margin-bottom: 12px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">1 - AJUDANTES</span>
              Cobrado um valor fixo acordado por profissional. Os mesmos atuam exclusivamente para carga e descarga de materiais no local de retirada e/ou entrega. A jornada padrão máxima é de 08h diárias, ultrapassado este limite será tarifada nova diária.
            </div>

            <div style="margin-bottom: 12px; padding-left: 15px; background-color: #f8fafc; border-left: 3px solid #dc2626; padding-top: 6px; padding-bottom: 6px;">
              <strong style="color: #0f172a;">1.1 - OPERACIONALIDADE DE CARGA E DESCARGA</strong><br>
              A obra deverá proporcionar aos colaboradores local seguro, sinalizado e acessível, a no máximo 10 metros do veículo.<br>
              <strong style="color: #dc2626;">Restrição técnica:</strong> Não contempla subir ou descer escadas/andares, nem transbordo manual de longa distância.
            </div>

            <div style="margin-bottom: 12px; padding-left: 15px;">
              <strong style="color: #0f172a;">1.2 - DIÁRIAS COMPLEMENTARES</strong><br>
              Serviços internos executados no canteiro de obras sem ligação direta com o transporte do dia serão pactuados previamente por escrito e inseridos de forma destacada na medição mensal.
            </div>

            <div style="margin-bottom: 12px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">2 - CARGA HORÁRIA E ESTADIA DO FRETE</span>
              A operação do transporte em viagem é de no máximo 8h da apresentação até liberação. O excedente gera nova ordem de serviço. Pernoites de carga geram cobrança de tarifa extra estipulada de 50% de taxa de pernoite mais a descarga subsequente.
            </div>

            <div style="margin-bottom: 12px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">3 - TRÂNSITO EM ÁREA RESTRITA (ZMRC / ZMRF)</span>
              Acesso a vias restritas exige descarga e evasão do veículo até as 15:00. Caso ocorra retenção por parte do contratante, os custos de multa, estadias ou diárias adicionais com acréscimo de 50% serão repassados integralmente.
            </div>

            <div style="margin-bottom: 12px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">4 - TAXAS E PEDÁGIOS</span>
              Custos de pedágios não incluídos expressamente nos itens de tarifa comercial serão cobrados mediante repasse real dos comprovantes na medição.
            </div>

            <div style="margin-bottom: 12px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">5 - PERÍMETRO EXTRAORDINÁRIO</span>
              Destinações fora da região metropolitana de São Paulo capital terão acréscimo de 30% a título de taxa de perímetro extraordinário, exceto quando o município estiver previamente listado no escopo comercial.
            </div>

            <div style="margin-bottom: 12.5px;">
              <span style="background-color: #0f172a; color: #ffffff; padding: 2px 8px; font-weight: bold; font-size: 9.5px; border-radius: 3px; margin-right: 6px;">6 - REAJUSTE DE CONTRATO</span>
              Os preços pactuados serão reajustados a cada 6 meses com base na variação positiva do INCC, ou extraordinariamente em caso de altas repentinas e imprevistas de combustíveis superiores a 10%.
            </div>
          `}
        </div>

        <!-- CONDICOES DE PAGAMENTO section -->
        <div style="margin-top: 20px; margin-bottom: 30px;">
          <div style="background-color: #f8fafc; border: 1px solid #cbd5e1; border-left: 4px solid #dc2626; border-radius: 6px; padding: 15px;">
            <div style="font-size: 11px; font-weight: bold; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
              Condições de Pagamento e Faturamento:
            </div>
            <div style="font-size: 11.5px; color: #334155; line-height: 1.5; font-weight: 500;">
              ${proposta.condicoes_pagamento ? proposta.condicoes_pagamento : `
                • Faturamento quinzenal após execução dos serviços mediante emissão de Nota Fiscal de Serviços eletrônica e boleto bancário com vencimento para 15 dias.
              `}
            </div>
          </div>
          <div style="font-size: 12px; font-weight: bold; color: #0f172a; margin-top: 18px; text-align: left; padding-left: 5px;">
            De acordo e Ciente do teor comercial e operacional: _________________________________________
          </div>
        </div>

        <!-- Signature Block -->
        <div style="border-top: 1px solid #e2e8f0; padding-top: 30px; display: flex; justify-content: space-between; gap: 50px;">
          <div style="flex: 1; text-align: center;">
            <div style="height: 1px; background-color: #475569; width: 100%; margin-bottom: 8px;"></div>
            <div style="font-size: 12px; font-weight: bold; color: #0f172a;">Rafael Gonçalves</div>
            <div style="font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: bold; letter-spacing: 0.5px; margin-top: 2px;">Diretoria Comercial • VOOPE TWO</div>
          </div>
          <div style="flex: 1; text-align: center;">
            <div style="height: 1px; background-color: #475569; width: 100%; margin-bottom: 8px;"></div>
            <div style="font-size: 12px; font-weight: bold; color: #0f172a;">${clientObra}</div>
            <div style="font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: bold; letter-spacing: 0.5px; margin-top: 2px;">Representante Legal • CONTRATANTE</div>
          </div>
        </div>
      </div>

      <!-- Footer with Page 4 mark -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px;">
        <span style="font-size: 10px; color: #94a3b8; font-weight: bold;">VOOPE TWO TRANSPORTES</span>
        <div style="background-color: #0f172a; color: #ffffff; font-weight: bold; font-size: 12px; width: 30px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 4px;">
          4
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageIds = ['proposta-page-1', 'proposta-page-2', 'proposta-page-3', 'proposta-page-4'];

    for (let index = 0; index < pageIds.length; index++) {
      const element = document.getElementById(pageIds[index]);
      if (element) {
        // html2canvas rendering parameters for ultra-sharp vectors and text
        const canvas = await html2canvas(element, {
          scale: 2.2, // increased scale for crystal-clear texts and graphics
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210; // A4 width in mm
        const imgHeight = 297; // A4 height in mm

        if (index > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      }
    }

    const docName = `Proposta_Comercial_VOOPE_#${proposta.id || 'N_A'}.pdf`;
    pdf.save(docName);
  } catch (err) {
    console.error('Erro ao gerar PDF da Proposta Comercial:', err);
    alert('Não foi possível gerar o PDF da Proposta Comercial. Tente novamente.');
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}
